<template>
  <div class="tracking-page-content">
    <!-- Main Hero Section -->
    <section
      class="hero-container"
      :style="{
        '--bg-desktop': `url(${desktopImage})`,
        '--bg-mobile': `url(${mobileImage})`
      }"
    >
      <div class="hero-overlay" />

      <div class="hero-content">
        <!-- 1. Search Card -->
        <div class="track-card">
          <!-- Top Badge Icon -->
          <div class="track-badge-icon">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#A30014"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line
                x1="12"
                y1="22.08"
                x2="12"
                y2="12"
              />
              <circle
                cx="18"
                cy="18"
                r="3"
              />
              <line
                x1="20.2"
                y1="20.2"
                x2="22"
                y2="22"
              />
            </svg>
          </div>

          <h1 class="track-title text-center mt-2">
            Track Your Order
          </h1>
          <p class="track-subtitle text-center">
            Enter your waybill number to get real-time updates on your order.
          </p>

          <!-- Form -->
          <b-form
            class="mt-2"
            @submit.prevent="onSearch"
          >
            <b-form-input
              v-model="waybill"
              class="custom-search-input"
              placeholder="Enter Waybill Number"
              required
            />

            <b-button
              type="submit"
              class="custom-submit-btn w-100 mt-2 d-flex align-items-center justify-content-center"
              :disabled="loading"
            >
              <b-spinner
                v-if="loading"
                small
                class="mr-2"
              />
              <template v-else>
                <svg
                  class="location-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle
                    cx="12"
                    cy="10"
                    r="3"
                  />
                </svg>
                <span>Track</span>
              </template>
            </b-button>
          </b-form>
        </div>

        <!-- 2. Feature Cards -->
        <div class="features-wrapper">
          <!-- Feature 1: Secure & Reliable -->
          <div class="feature-card">
            <div class="feature-badge">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#A30014"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
            </div>
            <div class="feature-text">
              <span class="feature-title">Secure & Reliable</span>
              <span class="feature-desc">Your Order is safe with us</span>
            </div>
          </div>

          <!-- Feature 2: Real-time Updates -->
          <div class="feature-card">
            <div class="feature-badge">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#A30014"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div class="feature-text">
              <span class="feature-title">Real-time Updates</span>
              <span class="feature-desc">Track your order live</span>
            </div>
          </div>

          <!-- Feature 3: Need Help? -->
          <div class="feature-card">
            <div class="feature-badge">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#A30014"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
              </svg>
            </div>
            <div class="feature-text">
              <span class="feature-title">Need Help?</span>
              <span class="feature-desc">Contact our support team</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Order Details & Timeline Section -->
    <div
      v-if="order || timelineData.length > 0"
      id="timeline"
      class="timeline-container py-5"
    >
      <div class="container">
        <!-- Order Summary Card -->
        <div
          v-if="order"
          class="order-summary-card mb-4"
        >
          <div class="d-flex flex-wrap justify-content-between align-items-center pb-3 border-bottom mb-3">
            <div>
              <span class="badge badge-light-danger text-uppercase px-2 py-1 mb-2 font-weight-bold">Waybill</span>
              <h2 class="order-waybill-no mb-0">
                {{ order.waybill_number }}
              </h2>
            </div>
            <div class="text-md-right mt-2 mt-md-0">
              <span class="order-meta-highlight">COD: <strong>{{ order.cod }}</strong></span>
              <span class="mx-2 text-muted">|</span>
              <span class="order-meta-highlight">Weight: <strong>{{ order.weight }}</strong></span>
            </div>
          </div>

          <div class="row">
            <div class="col-sm-6 mb-2 mb-sm-0">
              <p class="mb-1 text-muted small text-uppercase font-weight-bold">
                Merchant
              </p>
              <p class="font-weight-semibold mb-0 text-dark">
                {{ order.merchant }}
              </p>
            </div>
            <div class="col-sm-6">
              <p class="mb-1 text-muted small text-uppercase font-weight-bold">
                Customer
              </p>
              <p class="font-weight-semibold mb-0 text-dark">
                {{ order.customer_name }}
              </p>
            </div>
          </div>
        </div>

        <!-- Timeline Card -->
        <div
          v-if="timelineData.length > 0"
          class="timeline-card p-4"
        >
          <h4 class="font-weight-bold mb-4 text-dark">
            Tracking History
          </h4>
          <app-timeline>
            <app-timeline-item
              v-for="(item, index) in timelineData"
              :key="index"
              :variant="`${item.status.color || 'primary'}`"
              :icon="item.status.icon"
            >
              <div class="d-flex flex-sm-row flex-column flex-wrap justify-content-between align-items-start align-items-md-center mb-1">
                <div>
                  <h5 class="timeline-step-title mb-1 font-weight-bold">
                    {{ item.status.name }}
                  </h5>
                  <div class="timeline-details text-muted small">
                    <p
                      v-if="item.Reason"
                      class="mb-1"
                    >
                      Reason: {{ item.Reason }}
                    </p>
                    <p
                      v-if="item['Returned Branch']"
                      class="mb-1"
                    >
                      Returned Branch: {{ item['Returned Branch'] }}
                    </p>
                    <p
                      v-if="item['Assigned Rider']"
                      class="mb-1"
                    >
                      Assigned Rider: {{ item['Assigned Rider'] }}
                    </p>
                    <p
                      v-if="item['Received Branch'] && !item['Temporary Warehouse']"
                      class="mb-1"
                    >
                      Received Branch: {{ item['Received Branch'] }}
                    </p>
                    <p
                      v-if="item['Dispatched Warehouse']"
                      class="mb-1"
                    >
                      Dispatched Warehouse: {{ item['Dispatched Warehouse'] }}
                    </p>
                    <p
                      v-if="item['Rescheduled Date']"
                      class="mb-1"
                    >
                      Rescheduled Date: {{ formatDate(item['Rescheduled Date']) }}
                    </p>
                    <p
                      v-if="item['Changed City']"
                      class="mb-1"
                    >
                      Changed City: {{ item['Changed City'] }}
                    </p>
                    <p
                      v-if="item['Changed Warehouse']"
                      class="mb-1"
                    >
                      Changed Warehouse: {{ item['Changed Warehouse'] }}
                    </p>
                    <p
                      v-if="item['Suggested Warehouse']"
                      class="mb-1"
                    >
                      Suggested Warehouse: {{ item['Suggested Warehouse'] }}
                    </p>
                    <p
                      v-if="item['Temporary Warehouse']"
                      class="mb-1"
                    >
                      Temporary Warehouse: {{ item['Temporary Warehouse'] }}
                    </p>
                  </div>
                </div>
                <small class="text-muted mt-1 mt-sm-0">
                  {{ __dateTimeFormatter(item.date_time) }} | {{ item.date_time_ago }}
                </small>
              </div>
            </app-timeline-item>
          </app-timeline>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BFormInput,
  BButton,
  BForm,
  BSpinner,
} from 'bootstrap-vue'
import ErrorMessage from '@core/mixins/ErrorMessage'
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import { bucketUrl } from '@/constants/config'

const trackingRepository = RepositoryFactory.get('tracking')

export default {
  components: {
    BFormInput,
    BButton,
    BForm,
    BSpinner,
    AppTimeline,
    AppTimelineItem,
  },
  mixins: [ErrorMessage],
  data() {
    return {
      order: null,
      timelineData: [],
      loading: false,
      waybill: null,
      apiTrackingImage: null,
    }
  },
  computed: {
    // If API returned an image, use it. Otherwise, use default landscape.
    desktopImage() {
      return this.apiTrackingImage || '/DefaultTrackingImage.png'
    },
    // If API returned an image, use it. Otherwise, use default portrait.
    mobileImage() {
      return this.apiTrackingImage || '/DefaultTrackingImageMobile.png'
    },
  },
  mounted() {
    this.getOrderTrackingImage()
  },
  methods: {
    async getOrderTrackingInfo(payload) {
      try {
        this.loading = true
        const { data } = await trackingRepository.getOrderTrackingInfo(payload)
        this.timelineData = data.data.timeline.reverse()
        this.order = data.data.order
        this.loading = false
      } catch (e) {
        this.timelineData = []
        this.loading = false
        this.convertAndNotifyError(e)
      }
    },
    async onSearch() {
      const payload = {
        waybill_number: this.waybill,
      }
      await this.getOrderTrackingInfo(payload)
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    scrollToBottom() {
      const el = document.getElementById('timeline')
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
      }
    },
    async getOrderTrackingImage() {
      try {
        const { data } = await trackingRepository.getTrackingPublicSettingsResource()
        let imagePath = data.data.tracking_page_image
        if (imagePath && !imagePath.startsWith('/')) {
          imagePath = `/${imagePath}`
        }
        const baseUrl = bucketUrl.endsWith('/') ? bucketUrl.slice(0, -1) : bucketUrl
        this.apiTrackingImage = imagePath ? `${baseUrl}${imagePath}` : null
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
/* Main Page Content Wrapper */
.tracking-page-content {
  width: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

/* ========================================================
   HERO SECTION
   ======================================================== */
.hero-container {
  position: relative;
  flex: 1 1 auto;
  width: 100%;
  min-height: calc(100vh - 68px - 58px);
  background-image: var(--bg-desktop);
  background-size: cover !important;
  background-position: center center !important;
  background-repeat: no-repeat !important;
  background-color: #f3f5f8;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.5rem;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.02);
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 10;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ========================================================
   SEARCH CARD
   ======================================================== */
.track-card {
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 24px;
  padding: 2.2rem 2rem;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.track-badge-icon {
  width: 68px;
  height: 68px;
  background-color: #fde8ea;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.track-title {
  font-size: 1.85rem;
  font-weight: 800;
  color: #1a202c;
  letter-spacing: -0.5px;
}

.track-subtitle {
  color: #64748b;
  font-size: 0.92rem;
  line-height: 1.5;
  margin: 0.5rem auto 0;
  max-width: 290px;
}

/* Custom Input */
.custom-search-input {
  height: 50px;
  border-radius: 12px;
  border: 1.5px solid #e2e8f0;
  padding-left: 1.1rem;
  font-size: 0.95rem;
  color: #1e293b;
  transition: all 0.2s ease;
}

.custom-search-input:focus {
  border-color: #a30014;
  box-shadow: 0 0 0 3px rgba(163, 0, 20, 0.12);
}

.custom-search-input::placeholder {
  color: #94a3b8;
}

/* Custom Submit Button */
.custom-submit-btn {
  background-color: #a30014 !important;
  border: none !important;
  border-radius: 12px;
  height: 48px;
  font-weight: 700;
  font-size: 1.05rem;
  color: #ffffff;
  transition: all 0.2s ease;
}

.custom-submit-btn:hover {
  background-color: #8a0011 !important;
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(163, 0, 20, 0.25);
}

.location-icon {
  margin-right: 6px;
}

/* ========================================================
   FEATURE CARDS (DESKTOP)
   ======================================================== */
.features-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.25rem;
  margin-top: 4rem;
  width: 100%;
}

.feature-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 0.8rem 1.25rem;
  display: flex;
  align-items: center;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(226, 232, 240, 0.7);
  transition: transform 0.2s ease;
  width: 240px; /* Fixed desktop width */
  max-width: 100%;
}

.feature-card:hover {
  transform: translateY(-2px);
}

.feature-badge {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #ffffff;
  border: 1.5px solid #f8c9cf;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.feature-text {
  display: flex;
  flex-direction: column;
}

.feature-title {
  font-size: 0.9rem;
  font-weight: 800;
  color: #1e293b;
  line-height: 1.2;
}

.feature-desc {
  font-size: 0.76rem;
  color: #64748b;
  margin-top: 2px;
}

/* ========================================================
   TIMELINE & ORDER SUMMARY
   ======================================================== */
.timeline-container {
  background-color: #ffffff;
  border-top: 1px solid #edf0f2;
  width: 100%;
}

.order-summary-card {
  background-color: #f8fafc;
  border-radius: 16px;
  padding: 1.75rem;
  border: 1px solid #e2e8f0;
}

.order-waybill-no {
  font-weight: 800;
  font-size: 1.5rem;
  color: #1e293b;
}

.order-meta-highlight {
  font-size: 0.95rem;
  color: #334155;
}

.timeline-card {
  background-color: #ffffff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
}

.timeline-step-title {
  color: #1e293b;
  font-size: 1.15rem;
}

/* ========================================================
   RESPONSIVE (TABLET & MOBILE)
   ======================================================== */
@media (max-width: 991.98px), (orientation: portrait) and (max-width: 1024px) {
  .hero-container {
    background-image: var(--bg-mobile) !important;
    background-size: cover !important;
    background-position: center center !important;
    min-height: calc(100vh - 68px - 58px);
    padding: 2rem 1.5rem;
  }

  /* Vertical stack: centers cards without stretching */
  .features-wrapper {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-top: 5rem;
    width: 100%;
  }

  /* Keeps the exact compact desktop width (290px) and reduces vertical size */
  .feature-card {
    width: 290px !important;
    max-width: 100% !important;
    padding: 0.6rem 1rem !important;
    border-radius: 14px;
  }

  .feature-badge {
    width: 36px;
    height: 36px;
    margin-right: 10px;
  }

  .feature-badge svg {
    width: 18px;
    height: 18px;
  }

  .feature-title {
    font-size: 0.85rem;
  }

  .feature-desc {
    font-size: 0.74rem;
  }
}

@media (max-width: 576px) {
  .hero-container {
    min-height: calc(100vh - 60px - 58px);
    padding: 1.5rem 1rem 2rem 1rem;
  }

  .track-card {
    padding: 1.75rem 1.25rem;
    border-radius: 20px;
    max-width: 100%;
  }

  .track-badge-icon {
    width: 60px;
    height: 60px;
  }

  .track-title {
    font-size: 1.55rem;
  }

  .track-subtitle {
    font-size: 0.88rem;
  }

  .features-wrapper {
    gap: 1rem;
    margin-top: 4.5rem;
  }

  .feature-card {
    width: 240px !important;
    padding: 0.55rem 0.95rem !important;
  }
}
</style>
